import { useState } from 'react';

import { faChevronRight, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDrop } from 'react-dnd';
import { styled } from 'style/ORSNNTheme';
import { Button } from 'ui-kit';

import { LoanField } from '__generated__/globalTypes';

import { ItemTypes } from './common';
import { FieldIdAndLabel, FieldsMap } from './MapRequiredFieldsCard';
import { SearchBox } from './SearchBox';

const fieldItemWidthInPixels = 288;

// Allow precisely two items to show side-by-side.
const fieldGroupContainerWidthInPixels = fieldItemWidthInPixels * 2;

const StyledFieldsListWrapper = styled.div`
  color: ${(props) => props.theme.color.gray300};
  font-size: 12px;
  min-width: 588px; // Wide enough for two columns plus some padding.
`;

const FieldList = styled.div`
  height: 540px;
  overflow-y: auto;
  .isOver {
    background-color: ${(props) => props.theme.color.accentMuted};
    border-width: 1px;
    border-style: dashed;
    border-color: ${(props) => props.theme.color.themeAccentEmphasis};
    border-radius: 4px;
  }
`;

const StyledListItem = styled.div`
  min-height: 60px;
  width: ${fieldItemWidthInPixels}px;
  padding: 0 24px 0 22px;
  color: ${(props) => props.theme.color.slate50};
  line-height: 20px;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background-color: ${(props) => props.theme.color.slate400};
  }

  .mappedTo {
    color: ${(props) => props.theme.color.slate200};
  }
`;

const undoBtnClass = 'border border-slate-200 text-slate-200 bg-transparent hover:bg-transparent';

const FieldGroupContainer = styled.div`
  display: flex;
  width: ${fieldGroupContainerWidthInPixels}px;
  flex-wrap: wrap;
`;

type HeaderItem = {
  label: string;
};

type UnmappedFieldListItemProps = {
  field: FieldIdAndLabel;
  onHeaderDrop: (fieldId: LoanField, header: string) => void;
  disabled?: boolean;
};

const UnmappedFieldListItem = ({
  field,
  onHeaderDrop,
  disabled = false,
}: UnmappedFieldListItemProps) => {
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: ItemTypes.HEADER,
      drop: (item: HeaderItem) => onHeaderDrop(field.fieldId, item.label),
      canDrop: () => !disabled,
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
        canDrop: !!monitor.canDrop(),
      }),
    }),
    [disabled, field.fieldId, onHeaderDrop],
  );

  return (
    <StyledListItem ref={drop} className={isOver && canDrop ? 'isOver' : ''}>
      <div>
        <div>{field.label}</div>
      </div>
    </StyledListItem>
  );
};

type MappedFieldListItemProps = {
  field: FieldIdAndLabel;
  onUnmap: (fieldId: LoanField) => void;
  mappedHeader?: string;
  disabled?: boolean;
};

const MappedFieldListItem = ({
  field,
  onUnmap,
  mappedHeader,
  disabled = false,
}: MappedFieldListItemProps) => {
  return (
    <StyledListItem data-testid={`mapped-field-row-${field.fieldId}`}>
      <div>
        <div>{field.label}</div>
        <div className="mappedTo">{mappedHeader}</div>
      </div>
      <Button
        label="undo"
        size="small"
        type="secondary"
        className={undoBtnClass}
        disabled={disabled}
        onClick={() => onUnmap(field.fieldId)}
        data-testid={`mapped-field-undo-${field.fieldId}`}
      >
        UNDO
      </Button>
    </StyledListItem>
  );
};

const FieldGroupHeader = styled.div`
  padding: 5px 24px 5px 20px;
  color: ${(props) => props.theme.color.gray500};
  line-height: 20px;
  font-size: 12px;
  background-color: ${(props) => props.theme.color.bgSurface};

  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background-color: ${(props) => props.theme.color.slate400};
  }
`;

type FieldGroupProps = {
  fields: FieldIdAndLabel[];
  map: FieldsMap;
  searchTerm?: string;
  onUnmap: (fieldId: LoanField) => void;
  onMap: (fieldId: LoanField, header: string) => void;
  header: string;
  disabled?: boolean;
};

const FieldGroup = ({
  fields,
  map,
  searchTerm,
  onMap,
  onUnmap,
  header,
  disabled = false,
}: FieldGroupProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const matchesSearchText = (field: FieldIdAndLabel) => {
    if (!searchTerm) return true;
    return field.label.toLowerCase().includes(searchTerm?.toLowerCase());
  };

  const matchingFields = fields
    .filter(matchesSearchText)
    .sort((a, b) => a.label.localeCompare(b.label));
  const unmappedFields = matchingFields.filter((field) => !map[field.fieldId]);
  const mappedFields = matchingFields.filter((field) => map[field.fieldId]);

  const toggleIsOpen = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <FieldGroupHeader onClick={toggleIsOpen}>
        {header}
        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronRight} />
      </FieldGroupHeader>
      {isOpen && (
        <FieldGroupContainer>
          {mappedFields.map((field) => {
            return (
              <MappedFieldListItem
                key={field.fieldId}
                field={field}
                mappedHeader={map[field.fieldId]}
                onUnmap={onUnmap}
                disabled={disabled}
              />
            );
          })}
          {unmappedFields.map((field) => {
            return (
              <UnmappedFieldListItem
                key={field.fieldId}
                field={field}
                onHeaderDrop={onMap}
                disabled={disabled}
              />
            );
          })}
        </FieldGroupContainer>
      )}
    </>
  );
};

const FieldsList = ({
  requiredFields,
  optionalFields,
  map,
  onUnmap,
  onMap,
  disabled = false,
}: {
  requiredFields: FieldIdAndLabel[];
  optionalFields: FieldIdAndLabel[];
  map: FieldsMap;
  onUnmap: (fieldId: LoanField) => void;
  onMap: (fieldId: LoanField, header: string) => void;
  disabled?: boolean;
}) => {
  const [searchTerm, setSearchTerm] = useState<string>();

  const handleSearchTermChanged = (text: string) => {
    setSearchTerm(text);
  };

  return (
    <StyledFieldsListWrapper>
      <p className="mb-1">Search for an ORSNN column to map to</p>
      <SearchBox
        placeholder="Filter fields by name"
        onSearchTermChanged={handleSearchTermChanged}
      />
      <FieldList className="py-2 ">
        <FieldGroup
          header="REQUIRED"
          fields={requiredFields}
          map={map}
          searchTerm={searchTerm}
          onMap={onMap}
          onUnmap={onUnmap}
          disabled={disabled}
        />
        <FieldGroup
          header="OPTIONAL"
          fields={optionalFields}
          map={map}
          searchTerm={searchTerm}
          onMap={onMap}
          onUnmap={onUnmap}
          disabled={disabled}
        />
      </FieldList>
    </StyledFieldsListWrapper>
  );
};

export default FieldsList;
